import { useCallback, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './BottomSheet.module.scss';
import { clsx } from 'clsx';

export type BottomSheetProps = {
  /** Controlled open */
  open: boolean;

  /** Controlled setOpen */
  onOpenChange: (next: boolean) => void;

  /** Title for screen readers (hidden visually but accessible) */
  ariaLabel?: string;

  /** Description for screen readers (hidden visually but accessible) */
  ariaDescription?: string;

  /** Close when clicking the overlay */
  closeOnOverlay?: boolean;

  /** Enable drag-to-close gesture on the sheet */
  enableDrag?: boolean;

  /** Height mode: 'auto' fits content up to max-height; 'fullscreen' makes it 100dvh */
  mode?: 'auto' | 'fullscreen';

  /** ClassName passthrough for the content container */
  className?: string;

  /** Children content */
  children: React.ReactNode;

  /** Optional footer (e.g., action buttons) pinned at bottom */
  footer?: React.ReactNode;

  /** Initial focus ref if you want to focus a specific element inside */
  initialFocusRef?: React.RefObject<HTMLElement | HTMLButtonElement | null>;
};

const SPRING = {
  type: 'spring',
  stiffness: 500,
  damping: 40,
  mass: 0.8,
} as const;

export function BottomSheet({
  open,
  onOpenChange,
  ariaLabel,
  ariaDescription,
  closeOnOverlay = true,
  enableDrag = true,
  mode = 'auto',
  className,
  children,
  footer,
  initialFocusRef,
}: BottomSheetProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const motionClassName = clsx(
    styles.sheet,
    mode === 'fullscreen' ? styles.fullscreen : styles.auto,
    className
  );

  // Keyboard-aware safe area on iOS: apply env(safe-area-inset-bottom)
  // (Already handled via CSS, but we measure viewport units quirks on iOS if needed.)

  const handleOverlayClick = () => {
    if (closeOnOverlay) {
      onOpenChange(false);
    }
  };

  const onDragEnd = useCallback(
    (
      _: MouseEvent | TouchEvent | PointerEvent,
      info: { offset: { y: number }; velocity: { y: number } }
    ) => {
      // Close if dragged down enough or with enough velocity.
      const dragged = info.offset.y;
      const velocity = info.velocity.y;
      const distanceThreshold = 120; // px
      const velocityThreshold = 800; // px/s
      if (dragged > distanceThreshold || velocity > velocityThreshold) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  // Focus the provided element on open
  useEffect(() => {
    if (open && initialFocusRef?.current) {
      // Small timeout to ensure portal content exists
      const t = setTimeout(() => initialFocusRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, initialFocusRef]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Overlay */}
            <Dialog.Overlay asChild>
              <motion.div
                aria-hidden
                className={styles.overlay}
                onClick={handleOverlayClick}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            </Dialog.Overlay>

            {/* Content */}
            <Dialog.Content asChild className={styles.sheetContent}>
              <motion.div
                ref={contentRef}
                className={motionClassName}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={SPRING}
                drag={enableDrag ? 'y' : false}
                dragDirectionLock={enableDrag}
                dragElastic={0.12}
                dragMomentum={enableDrag}
                dragConstraints={{ top: 0, bottom: 0 }}
                onDragEnd={enableDrag ? onDragEnd : undefined}
              >
                <div className={styles.handle} aria-hidden>
                  <span className={styles.handleBar} />
                </div>

                {ariaLabel && (
                  <VisuallyHidden asChild>
                    <Dialog.Title>{ariaLabel}</Dialog.Title>
                  </VisuallyHidden>
                )}

                {ariaDescription && (
                  <VisuallyHidden asChild>
                    <Dialog.Description>{ariaDescription}</Dialog.Description>
                  </VisuallyHidden>
                )}

                <div className={styles.body}>{children}</div>

                {footer && <footer className={styles.footer}>{footer}</footer>}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

export default BottomSheet;
